import { useVerifyContactCodeMutation } from '../../apis/contactVerifyApi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUsername } from '../useUsersSlice';
import { AlertType, TowerAlert } from '../../../../common/Alerts';
import { includes } from 'lodash';

const ERROR_ATTEMPTS = 5;
const MAX_ATTEMPTS = 10;
const DELAY_MS = 3000;

// @ts-ignore
const hasVerifiedCodeOnLoad = window.HAS_VERIFIED || false;
// @ts-ignore
const errorMessageOnLoad = window.ERROR_MESSAGE || '';

const useVerifyCode = () => {
    const { username: email } = useUsername();
    const [hasVerified, setHasVerified] = useState(hasVerifiedCodeOnLoad);
    const [isDelaying, setIsDelaying] = useState(false);
    const [hasExpiredCode, setHasExpiredCode] = useState(false);
    const [alerts, setAlerts] = useState<TowerAlert[]>([]);

    useEffect(() => {
        if (errorMessageOnLoad) {
            setAlerts([{ type: AlertType.error, message: errorMessageOnLoad }]);
            setHasVerified(false);
            setHasExpiredCode(true);
        }
    }, []);

    const [verifyMutation, { isLoading: mutating, isSuccess, isError, error, reset }] =
        useVerifyContactCodeMutation();

    const attempts = useRef(0);

    // handle server success / error
    useEffect(() => {
        if (isSuccess) {
            setHasVerified(true);
        }
        if (isError) {
            let newAlerts = [
                { type: AlertType.error, message: 'Your verification code is invalid.' },
            ];
            const isAccessDeniedStatus =
                (error as any)?.originalStatus === 401 || includes((error as any)?.data, '401');
            const isExpired = includes((error as any)?.data, 'expired');
            if (isAccessDeniedStatus && isExpired) {
                setHasVerified(false);
                setIsDelaying(false);
                setHasExpiredCode(true);
                newAlerts = [
                    {
                        type: AlertType.error,
                        message: 'Your verification code has expired.',
                    },
                ];
            }
            setAlerts(newAlerts);
        }
    }, [isSuccess, isError, error]);

    const loading = mutating || isDelaying;

    const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

    const verifyContactCode = async (code: string) => {
        if (loading || hasExpiredCode) {
            return;
        }

        // count call
        attempts.current += 1;
        if (attempts.current > MAX_ATTEMPTS) {
            setAlerts([
                {
                    type: AlertType.error,
                    message: 'Your code has expired.',
                },
            ]);
            return;
        }

        // If they have more than ERROR_ATTEMPTS unsuccessful attempts in a row, just delay. This is for security reasons so
        // that they will be throttled in their attempts in order to throw off any brute force attacks.
        if (attempts.current > ERROR_ATTEMPTS) {
            setIsDelaying(true);
            await sleep(DELAY_MS);
            setIsDelaying(false);
            return;
        }

        setAlerts([]);
        verifyMutation({ email, code });
    };

    const resetState = useCallback(() => {
        setHasVerified(false);
        setHasExpiredCode(false);
        setIsDelaying(false);
        attempts.current = 0;
        setAlerts([]);
        reset();
    }, [reset]);

    return {
        verifyContactCode,
        isLoading: loading,
        alerts,
        hasVerified,
        reset: resetState,
    };
};
export default useVerifyCode;
